import React, { useState } from 'react';
import RichTextEditor from '../RichTextEditor';
import {
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  QuestionMarkCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface Question {
  id: string;
  question: string; // This will be shown in the body field
  type: 'multiple_choice' | 'true_false' | 'fill_blank';
  options?: string[];
  correctAnswer: string | number;
  explanation?: string;
  showFullExplanation?: boolean; // For "Read More" functionality
}

interface QuizBuilderProps {
  onChange?: (quizData: any) => void;
  onQuestionChange?: (question: string) => void; // For auto-populating body
}

export default function QuizBuilder({ onChange, onQuestionChange }: QuizBuilderProps) {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [editingQuestion, setEditingQuestion] = useState<string | null>(null);

  const addQuestion = () => {
    const newQuestion: Question = {
      id: Date.now().toString(),
      question: '',
      type: 'multiple_choice',
      options: ['', '', '', ''],
      correctAnswer: 0,
      showFullExplanation: false,
    };
    const updatedQuestions = [...questions, newQuestion];
    setQuestions(updatedQuestions);
    setEditingQuestion(newQuestion.id);
    onChange?.({ questions: updatedQuestions });
  };

  const updateQuestion = (id: string, updates: Partial<Question>) => {
    const updated = questions.map(q => 
      q.id === id ? { ...q, ...updates } : q
    );
    setQuestions(updated);
    onChange?.({ questions: updated });
    
    // Auto-populate body with the first question's text
    if (updates.question !== undefined && updated.length > 0) {
      const firstQuestion = updated[0];
      if (firstQuestion.question.trim()) {
        onQuestionChange?.(firstQuestion.question);
      }
    }
  };

  const deleteQuestion = (id: string) => {
    const filtered = questions.filter(q => q.id !== id);
    setQuestions(filtered);
    onChange?.({ questions: filtered });
    
    // Update body with remaining first question or clear if none
    if (filtered.length > 0 && filtered[0].question.trim()) {
      onQuestionChange?.(filtered[0].question);
    } else {
      onQuestionChange?.('');
    }
  };

  const updateOption = (questionId: string, optionIndex: number, value: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.options) {
      const newOptions = [...question.options];
      newOptions[optionIndex] = value;
      updateQuestion(questionId, { options: newOptions });
    }
  };

  const addOption = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.options && question.options.length < 6) {
      const newOptions = [...question.options, ''];
      updateQuestion(questionId, { options: newOptions });
    }
  };

  const removeOption = (questionId: string, optionIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.options && question.options.length > 2) {
      const newOptions = question.options.filter((_, idx) => idx !== optionIndex);
      // Adjust correct answer if needed
      let newCorrectAnswer = question.correctAnswer;
      if (typeof newCorrectAnswer === 'number') {
        if (optionIndex === newCorrectAnswer) {
          newCorrectAnswer = 0;
        } else if (optionIndex < newCorrectAnswer) {
          newCorrectAnswer = newCorrectAnswer - 1;
        }
      }
      updateQuestion(questionId, { options: newOptions, correctAnswer: newCorrectAnswer });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Quiz Questions
        </h3>
        <button
          type="button"
          onClick={addQuestion}
          className="w-full sm:w-auto flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Add Question
        </button>
      </div>

      {questions.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600">
          <QuestionMarkCircleIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            No questions added yet. Click "Add Question" to create your first quiz question.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {questions.map((question, index) => (
            <div
              key={question.id}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6"
            >
              <div className="flex justify-between items-start mb-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Question {index + 1}
                </h4>
                <button
                  type="button"
                  onClick={() => deleteQuestion(question.id)}
                  className="text-red-500 hover:text-red-700 transition-colors"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Question Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Question Text
                    {index === 0 && (
                      <span className="text-xs text-blue-600 dark:text-blue-400 ml-2 font-medium">
                        (Main notification question)
                      </span>
                    )}
                  </label>
                  <RichTextEditor
                    value={question.question}
                    onChange={(value) => updateQuestion(question.id, { question: value })}
                    placeholder="Enter your quiz question..."
                    minHeight="120px"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {index === 0 
                      ? 'This question will appear in the system notification tray and app'
                      : 'Tip: Keep it concise for better mobile display'
                    }
                  </p>
                </div>

                {/* Question Type */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Type
                    </label>
                    <select
                      value={question.type}
                      onChange={(e) => updateQuestion(question.id, { 
                        type: e.target.value as Question['type'],
                        options: e.target.value === 'true_false' ? ['True', 'False'] : ['', '', '', '']
                      })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="multiple_choice">Multiple Choice</option>
                      <option value="true_false">True/False</option>
                      <option value="fill_blank">Fill in Blank</option>
                    </select>
                  </div>
                </div>

                {/* Answer Options */}
                {question.type === 'multiple_choice' && (
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Answer Options
                      </label>
                      {question.options && question.options.length < 6 && (
                        <button
                          type="button"
                          onClick={() => addOption(question.id)}
                          className="text-sm text-blue-600 hover:text-blue-700 flex items-center whitespace-nowrap"
                        >
                          <PlusIcon className="w-4 h-4 mr-1" />
                          Add Option
                        </button>
                      )}
                    </div>
                    <div className="space-y-2">
                      {question.options?.map((option, optIndex) => (
                        <div key={optIndex} className={`flex items-center space-x-2 p-2 rounded-lg ${
                          question.correctAnswer === optIndex ? 'bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700' : ''
                        }`}>
                          <span className="text-sm font-medium text-gray-500 w-6">
                            {String.fromCharCode(65 + optIndex)}
                          </span>
                          <input
                            value={option}
                            onChange={(e) => updateOption(question.id, optIndex, e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                            placeholder={`Enter option ${optIndex + 1}`}
                          />
                          <button
                            type="button"
                            onClick={() => updateQuestion(question.id, { correctAnswer: optIndex })}
                            className={`px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                              question.correctAnswer === optIndex
                                ? 'bg-green-600 text-white hover:bg-green-700'
                                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                            }`}
                          >
                            {question.correctAnswer === optIndex ? (
                              <span className="flex items-center">
                                <CheckCircleIcon className="w-4 h-4 mr-1" />
                                Correct
                              </span>
                            ) : (
                              'Set as Correct'
                            )}
                          </button>
                          {question.options && question.options.length > 2 && (
                            <button
                              type="button"
                              onClick={() => removeOption(question.id, optIndex)}
                              className="p-1 text-red-500 hover:text-red-700 transition-colors"
                              title="Remove option"
                            >
                              <XMarkIcon className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {question.type === 'true_false' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Correct Answer
                    </label>
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={`tf-${question.id}`}
                          checked={question.correctAnswer === 'True'}
                          onChange={() => updateQuestion(question.id, { correctAnswer: 'True' })}
                          className="mr-2"
                        />
                        <span className="text-gray-700 dark:text-gray-300">True</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={`tf-${question.id}`}
                          checked={question.correctAnswer === 'False'}
                          onChange={() => updateQuestion(question.id, { correctAnswer: 'False' })}
                          className="mr-2"
                        />
                        <span className="text-gray-700 dark:text-gray-300">False</span>
                      </label>
                    </div>
                  </div>
                )}

                {question.type === 'fill_blank' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Correct Answer
                    </label>
                    <input
                      value={question.correctAnswer}
                      onChange={(e) => updateQuestion(question.id, { correctAnswer: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter the correct answer..."
                    />
                  </div>
                )}

                {/* Explanation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Explanation (Optional)
                  </label>
                  <RichTextEditor
                    value={question.explanation || ''}
                    onChange={(value) => updateQuestion(question.id, { explanation: value })}
                    placeholder="Explain why this answer is correct..."
                    minHeight="100px"
                  />
                  <div className="mt-2 flex items-center justify-between">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {question.explanation?.length || 0} characters
                      {(question.explanation?.length || 0) > 150 && ' (Will show "Read More" in app)'}
                    </p>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={question.showFullExplanation || false}
                        onChange={(e) => updateQuestion(question.id, { showFullExplanation: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        Always show full explanation
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quiz Summary */}
      {questions.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center">
                <QuestionMarkCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2" />
                <span className="text-xs sm:text-sm text-blue-600 dark:text-blue-400">
                  {questions.length} question{questions.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}